import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  FileText, 
  Download, 
  Edit3, 
  CheckCircle, 
  Lock, 
  Eye,
  Loader2,
  X,
  Save,
  Bold,
  Italic,
  List,
  ListOrdered
} from 'lucide-react';
import { toast } from 'sonner';
import { authenticatedFetch } from '../utils/api';

const ReportEditor = ({ 
  isOpen, 
  onClose, 
  reportData, 
  reportType,
  onReportUpdated 
}) => {
  const [coverPageHtml, setCoverPageHtml] = useState('');
  const [reportBodyHtml, setReportBodyHtml] = useState('');
  const [reportState, setReportState] = useState('DRAFT');
  const [reportId, setReportId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [finalizedMeta, setFinalizedMeta] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (reportData) {
      setCoverPageHtml(reportData.cover_page_html || '');
      setReportBodyHtml(reportData.report_body_html || '');
      setReportState(reportData.state || 'DRAFT');
      setReportId(reportData.report_id || null);
      setFinalizedMeta(reportData.finalized_meta || null);
    }
  }, [reportData]);

  // Apply formatting to selected text
  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    // Update state from contentEditable
    if (editorRef.current) {
      setReportBodyHtml(editorRef.current.innerHTML);
    }
  };

  // Handle content change in editor
  const handleEditorChange = () => {
    if (editorRef.current) {
      setReportBodyHtml(editorRef.current.innerHTML);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/save-report-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          report_id: reportId,
          report_type: reportType,
          cover_page_html: coverPageHtml,
          report_body_html: reportBodyHtml,
          state: 'DRAFT'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReportId(data.report_id);
        toast.success('Draft saved successfully');
      } else {
        throw new Error('Failed to save draft');
      }
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReviewed = async () => {
    setSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/save-report-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          report_id: reportId,
          report_type: reportType,
          cover_page_html: coverPageHtml,
          report_body_html: reportBodyHtml,
          state: 'REVIEWED'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReportId(data.report_id);
        setReportState('REVIEWED');
        toast.success('Report marked as reviewed');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!window.confirm('Are you sure you want to finalize this report? Once finalized, it cannot be edited.')) {
      return;
    }

    setFinalizing(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/finalize-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          report_id: reportId,
          report_type: reportType,
          cover_page_html: coverPageHtml,
          report_body_html: reportBodyHtml
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReportState('FINALIZED');
        setFinalizedMeta({
          finalized_by: data.finalized_by,
          finalized_at: data.finalized_at
        });
        toast.success('Report finalized successfully. You can now download the PDF.');
      } else {
        throw new Error('Failed to finalize report');
      }
    } catch (error) {
      toast.error('Failed to finalize report');
    } finally {
      setFinalizing(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (reportState !== 'FINALIZED') {
      toast.error('Please finalize the report before downloading PDF');
      return;
    }

    setGeneratingPdf(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/generate-report-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          report_id: reportId,
          report_type: reportType
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Official_Report_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded successfully');
      } else {
        throw new Error('Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getStateBadge = () => {
    switch (reportState) {
      case 'DRAFT':
        return <Badge className="bg-yellow-500">DRAFT</Badge>;
      case 'REVIEWED':
        return <Badge className="bg-blue-500">REVIEWED</Badge>;
      case 'FINALIZED':
        return <Badge className="bg-green-600">FINALIZED</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const isEditable = reportState !== 'FINALIZED';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Official Report - {reportType === 'start_of_term' ? 'Start of Term' : 'End of Term'}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {getStateBadge()}
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {finalizedMeta && (
            <p className="text-xs text-gray-500 mt-1">
              Finalized by {finalizedMeta.finalized_by} on {new Date(finalizedMeta.finalized_at).toLocaleString()}
            </p>
          )}
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex gap-2 py-2 border-b">
          <Button
            variant={activeTab === 'preview' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('preview')}
          >
            <Eye className="h-4 w-4 mr-1" />
            Preview
          </Button>
          {isEditable && (
            <Button
              variant={activeTab === 'edit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('edit')}
            >
              <Edit3 className="h-4 w-4 mr-1" />
              Edit Report
            </Button>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto py-4">
          {activeTab === 'preview' ? (
            <div className="space-y-4">
              {/* Cover Page Preview */}
              <Card>
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-sm">Cover Page</CardTitle>
                </CardHeader>
                <CardContent className="p-4 bg-white border rounded-lg">
                  <div 
                    className="cover-page-preview"
                    dangerouslySetInnerHTML={{ __html: coverPageHtml }}
                    style={{ 
                      fontFamily: 'Times New Roman, serif',
                      minHeight: '400px',
                      padding: '20px',
                      border: '1px solid #e5e7eb'
                    }}
                  />
                </CardContent>
              </Card>

              {/* Report Body Preview */}
              <Card>
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-sm">Report Body</CardTitle>
                </CardHeader>
                <CardContent className="p-4 bg-white border rounded-lg">
                  <div 
                    className="report-body-preview"
                    dangerouslySetInnerHTML={{ __html: reportBodyHtml }}
                    style={{ 
                      fontFamily: 'Times New Roman, serif',
                      lineHeight: '1.5',
                      padding: '20px'
                    }}
                  />
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Edit Mode */
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Editing Mode:</strong> You can modify the report body below. Select text and use the toolbar to format. The cover page is auto-generated.
                </p>
              </div>
              
              <Card>
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-sm">Edit Report Body</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Simple Toolbar */}
                  <div className="flex gap-1 p-2 border-b bg-gray-50">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => applyFormat('bold')}
                      title="Bold"
                    >
                      <Bold className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => applyFormat('italic')}
                      title="Italic"
                    >
                      <Italic className="h-4 w-4" />
                    </Button>
                    <div className="w-px bg-gray-300 mx-1" />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => applyFormat('insertUnorderedList')}
                      title="Bullet List"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => applyFormat('insertOrderedList')}
                      title="Numbered List"
                    >
                      <ListOrdered className="h-4 w-4" />
                    </Button>
                    <div className="w-px bg-gray-300 mx-1" />
                    <select 
                      className="text-sm border rounded px-2"
                      onChange={(e) => applyFormat('formatBlock', e.target.value)}
                      defaultValue=""
                    >
                      <option value="" disabled>Heading</option>
                      <option value="h1">Heading 1</option>
                      <option value="h2">Heading 2</option>
                      <option value="h3">Heading 3</option>
                      <option value="p">Paragraph</option>
                    </select>
                  </div>
                  
                  {/* ContentEditable Editor */}
                  <div
                    ref={editorRef}
                    contentEditable
                    className="min-h-[500px] p-4 focus:outline-none prose max-w-none"
                    style={{ 
                      fontFamily: 'Times New Roman, serif',
                      lineHeight: '1.6'
                    }}
                    onInput={handleEditorChange}
                    onBlur={handleEditorChange}
                    dangerouslySetInnerHTML={{ __html: reportBodyHtml }}
                    suppressContentEditableWarning={true}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <DialogFooter className="border-t pt-4 flex-wrap gap-2">
          {isEditable ? (
            <>
              <Button 
                variant="outline" 
                onClick={handleSaveDraft}
                disabled={saving}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save Draft
              </Button>
              
              {reportState === 'DRAFT' && (
                <Button 
                  variant="outline"
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700"
                  onClick={handleMarkReviewed}
                  disabled={saving}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Mark as Reviewed
                </Button>
              )}
              
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleFinalize}
                disabled={finalizing || !reportId}
              >
                {finalizing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
                Finalize Report
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Lock className="h-4 w-4" />
              Report is finalized and locked for editing
            </div>
          )}
          
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={handleDownloadPdf}
            disabled={reportState !== 'FINALIZED' || generatingPdf}
          >
            {generatingPdf ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-1" />Generating PDF...</>
            ) : (
              <><Download className="h-4 w-4 mr-1" />Download PDF</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportEditor;
